import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

export type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const THEME_STORAGE_KEY = "intractive-theme";

export const AppThemeContext = createContext<ThemeContextValue | undefined>(
  undefined
);

const ThemeContext = AppThemeContext;

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
  preferredTheme?: "auto" | "light" | "dark";
  /**
   * Body theme to apply in place of plain `"light"` — e.g. a workspace
   * project's base brandkit id. `theme` state stays `"light"`/`"dark"` (the
   * toggle and consumers are unaffected); only the `data-theme` written to the
   * body is swapped, keeping it in sync with the SSR-rendered value.
   */
  lightTheme?: string;
  /**
   * Lock the theme to light (or `lightTheme`): the pages runtime offers no
   * dark mode, so we ignore the saved/system preference, never persist, and
   * `toggleTheme` is a no-op. This keeps the shared global preference (used by
   * the classic dashboard) untouched while a workspace project is open.
   */
  lockLight?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  preferredTheme = "auto",
  lightTheme,
  lockLight,
}) => {
  const [theme, setTheme] = useState<Theme>(() => {
    if (lockLight) return "light";

    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === "dark" || savedTheme === "light") {
      return savedTheme;
    }

    // If no saved theme, use preferredTheme or system preference
    if (preferredTheme === "light" || preferredTheme === "dark") {
      return preferredTheme;
    }

    // Auto mode: use system preference
    if (preferredTheme === "auto" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }

    return "light";
  });

  useEffect(() => {
    if (lockLight) {
      setTheme("light");
      return;
    }
    // Only set initial theme if no saved preference and preferredTheme is set
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (!savedTheme && preferredTheme !== "auto") {
      if (preferredTheme === "light" || preferredTheme === "dark") {
        setTheme(preferredTheme);
      }
    } else if (!savedTheme && preferredTheme === "auto" && window.matchMedia) {
      const systemPreference = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      setTheme(systemPreference);
    }
  }, [preferredTheme, lockLight]);

  useEffect(() => {
    const bodyTheme = theme === "light" && lightTheme ? lightTheme : theme;
    document.body.setAttribute("data-theme", bodyTheme);
    // Don't persist while locked — leave the user's global (dashboard)
    // light/dark choice as they left it.
    if (!lockLight) localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme, lightTheme, lockLight]);

  const toggleTheme = () => {
    if (lockLight) return;
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
